import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { object, mixed, string, date, boolean } from 'yup';
import { Container, Row, Col, Form } from "react-bootstrap";
import { sub } from 'date-fns';
import PropTypes from 'prop-types';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import QuoteResult from '../Helpers/QuoteResults';
import BasicInfo from './BasicInfo';
import SuppData from '../../../Helpers/Data/Products/Supplemental/SuppData';
import Age from '../../../Helpers/Dates/Age';
import { SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_EMAIL_CAPTURE, getBasicInfo, getSessionStorageItem, setBasicInfo } from '../../../../sessionStorage'
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.jsx';

import '../../Quoter.scss';
import './Quoter.scss';

export default function Quoter(props) {

  const formMethods = useForm({
    resolver: yupResolver(Quoter.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState,
  });

  const { handleSubmit, trigger, register, setValue, formState: { errors } } = formMethods;

  const [loading, setLoading] = useState(true);
  const [quoteRan, setQuoteRan] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [selected, setSelected] = useState(false);

  const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

  const suppData = SuppData(props);

  const emailCapture = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
  const [emailCaptureActive, setEmailCaptureActive] = useState(false);
  const [saveQuoteData, setSaveQuoteData] = useState(null);
  const [saveQuoteContact, setSaveQuoteContact] = useState(null);

  const handleValueChange = (field, value) => {
    props.onBasicInfoFieldChange(field, value);
    const basicInfoFromStorage = getBasicInfo();
    basicInfoFromStorage[field] = value;
    setBasicInfo(basicInfoFromStorage);
    trigger(field);
  };

  const handleSelectedValueChange = (value) => {
    setValue('selected', value);
    setSelected(value);
  };

  const handleSelectedQuote = (value) => {
    let form = { ...props.fullFormState };
    form.pages.quoter.selectedQuote = value;
    props.onArrayChange(form);
    setValue('selectedQuote', value);
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register('gender');
    setValue('gender', props.basicInfo.gender);
    register('birthdate');
    setValue('birthdate', props.basicInfo.birthdate);
    register('state');
    setValue('state', props.basicInfo.state);
    register('usResident');
    setValue('usResident', props.basicInfo.usResident);
    register('selected');
    setValue('selected', selected);
  }, []);

  const runQuote = (getIsSubscribed) => {
    setLoading(true);
    setQuoteRan(true);

    const age = Age(props.basicInfo.birthdate);

    fetch(`${covrServicesUrl}/api/Aflac/GetAflacQuotes?age=${age}&state=${props.basicInfo.state}`,
      {
        method: 'GET',
        headers: {
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        },
      })
      .then(res => res.json())
      .then(data => {
        if (getIsSubscribed()) {
          setQuoteResults(data);
          setLoading(false);
        }
      }).catch((error) => {
        if (getIsSubscribed()) {
          ErrorHandler(error, setQuoteResults);
          setLoading(false);
        }
      });
  };

  function delay(t, v) {
    return new Promise((resolve) => {
      setTimeout(resolve.bind(null, v), t);
    });
  }

  let debounce = null;
  useEffect(() => {
    setLoading(true);
    let isSubscribed1 = true;
    trigger("birthdate").then(async () => {
      if (isSubscribed1) {
        await delay(2000);

        if ((Object.keys(errors).length >= 1 && !errors.selected) || !props.basicInfo.state || !props.basicInfo.birthdate || props.basicInfo.birthdate.indexOf('_') > -1) {
          if (quoteResults.length) {
            setQuoteResults([]);
          }

          return;
        }
        setLoading(true);
        let isSubscribed = true;
        if (debounce) {
          clearTimeout(debounce);
        }
        debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);

        return () => {
          clearTimeout(debounce);
          isSubscribed = false;
        };
      }
    });

    return () => isSubscribed1 = false;

  }, [props.basicInfo.birthdate, props.basicInfo.state])

  return (
    <Container className="quoter aflac-quoter">
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(props.onNext)}>
          <Row>
            <Col lg={4} className="mb-3 w-100">
              <BasicInfo
                formState={props.basicInfo}
                onFieldChange={(field, value) => props.onBasicInfoFieldChange(field, value)}
                hideBackNext={true}
                ageSpecs={suppData.specs.age}
                logoBottom
                noValidate
                handleValueChange={handleValueChange}
                setModalSaveQuoteIsOpen={props.setModalSaveQuoteIsOpen}
                setSaveQuoteData={setSaveQuoteData}
                saveQuoteContact={saveQuoteContact}
                emailCapture={emailCapture}
                setEmailCaptureActive={setEmailCaptureActive}
                emailCaptureActive={emailCaptureActive}
              />
            </Col>
            <Col lg={8}>
              <QuoteResult
                state={props.basicInfo.state}
                formState={props.formState}
                onFieldChange={props.onFieldChange}
                result={quoteResults}
                quoteRan={quoteRan}
                quoteResults={quoteResults}
                loading={loading}
                onNext={props.onNext}
                error={errors.selected}
                selected={selected}
                setSelected={handleSelectedValueChange}
                handleSelectedQuote={handleSelectedQuote}
              />
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </Container>
  );
}

Quoter.ValidationSchema = (ageSpecs) => {
  return object().shape({
    require: boolean(),
    gender: string().required('Gender is required.'),
    birthdate: date()
      .transform(emptyStringToUndefined)
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    usResident: mixed().oneOf([true], 'You must be a US citizen or permanent resident.'),
    selected: mixed().oneOf([true], 'Please select a product.'),
  })
};

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  basicInfo: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBasicInfoFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  ageSpecs: PropTypes.object.isRequired,
  setSaveQuoteData: PropTypes.func,
  saveQuoteContact: PropTypes.object,
  emailCapture: PropTypes.object,
  setEmailCaptureActive: PropTypes.func,
  emailCaptureActive: PropTypes.bool,
};

Quoter.DefaultFormState = () => {
  return {
    specialNote: '',
    premium: null,
    carrierName: 'Aflac',
    term: 0,
    healthClass: '',
    product: 'Supplemental Insurance',
    selectedQuote: []
  }
}
